import { Component, OnInit, Input, Output, EventEmitter, ViewEncapsulation, OnDestroy } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';
import * as moment_ from 'moment';

import { AttributeDefinition } from '../../models/attribute-definition';
import { AttributeComponent } from '../../interfaces/attribute.component';
import { AttributeControlService } from '../../attribute-control-service';

const moment = moment_;

@Component({
  selector: 'app-time',
  templateUrl: './time.component.html',
  styleUrls: ['./time.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class TimeComponent implements OnInit, OnDestroy, AttributeComponent {
  @Input() formGroup: FormGroup;
  @Input() attributeDefinition: AttributeDefinition;
  @Output() onRemove = new EventEmitter<number>();

  format: 12 | 24 = 12;

  private sub?: Subscription;

  constructor(
    private _attributeControlService: AttributeControlService<string | null>
  ) {}

  ngOnInit(): void {
    const initial: string | null =
      this.attributeDefinition?.value != null ? String(this.attributeDefinition.value) : null;
    const normalized = this.normalize(initial);

    this._attributeControlService.addFormControl(
      this.attributeDefinition.formControlName,
      this.attributeDefinition.isRequired,
      normalized,
      this.formGroup
    );

    this.sub = this.control.valueChanges.subscribe(v => {
      const nv = this.normalize(v);
      if (nv !== v) this.control.setValue(nv, { emitEvent: false });
    });
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe?.();
    this._attributeControlService.removeControl(this.attributeDefinition.formControlName, this.formGroup);
  }

  value(): string | null {
    const normalized = this.normalize(this.control?.value);
    this.attributeDefinition.value = normalized;
    return normalized;
  }

  clear(ev?: MouseEvent): void {
    ev?.stopPropagation();
    this.control.setValue(null, { emitEvent: true });
    this.control.markAsDirty();
    this.control.markAsTouched();
    this.attributeDefinition.value = null;
  }

  private parseToMoment(v: any): moment_.Moment | null {
    if (v == null) return null;
    const s = String(v).trim();
    if (!s) return null;
    const looksIso = /\dT\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+\-]\d{2}:\d{2})?$/.test(s);
    if (looksIso) {
      const mz = moment.parseZone(s, moment.ISO_8601, true);
      return mz.isValid() ? mz : null;
    }
    const m = moment(s, ['HH:mm', 'H:mm', 'HH:mm:ss', 'H:mm:ss', 'h:mm A', 'h:mmA'], true);
    return m.isValid() ? m : null;
  }

  private normalize(v: any): string | null {
    const m = this.parseToMoment(v);
    return m ? m.format('HH:mm') : null;
  }

  get defaultTimeForPicker(): string | null {
    const m = this.parseToMoment(this.control?.value);
    if (!m) return null;
    return this.format === 24 ? m.format('HH:mm') : m.format('hh:mm A');
  }

  get control(): FormControl {
    return this.formGroup.get(this.attributeDefinition.formControlName) as FormControl;
  }

  get label(): string {
    return (this.attributeDefinition?.name || '').replace(/\s*[:*]+$/, '').trim();
  }

  getErrorMessage(): string {
    if (!this.control) return '';
    if (this.control.hasError('required')) return `${this.label} is required.`;
    return '';
  }

}
